import axios from 'axios';
import MockUserApiService from './MockUserApiService';

class BorisApiService {
  constructor() {
    this.apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1';
    this.sessionId = null;
    
    // Initialize mock service for development
    this.mockUserService = new MockUserApiService();
    
    // Setup axios defaults
    this.api = axios.create({
      baseURL: this.apiBase,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: parseInt(process.env.REACT_APP_API_TIMEOUT) || 30000, // 30 seconds timeout
    });

    // Request interceptor
    this.api.interceptors.request.use(
      (config) => {
        if (process.env.REACT_APP_DEBUG_API === 'true') {
          console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`, config.data);
        }
        return config;
      },
      (error) => {
        console.error('❌ Request Error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.api.interceptors.response.use(
      (response) => {
        if (process.env.REACT_APP_DEBUG_API === 'true') {
          console.log(`✅ API Response: ${response.status} ${response.config.url}`, response.data);
        }
        return response;
      },
      (error) => {
        console.error('❌ Response Error:', error.response?.data || error.message);
        return Promise.reject(error);
      }
    );
  }

  // Session Management
  async createSession(userId = null, intent = 'discover_category') {
    try {
      const response = await this.api.post('/chat/sessions', {
        user_id: userId,
        intent: intent,
        frontend_user_id: userId
      });
      
      this.sessionId = response.data.session_id;
      console.log(`🎯 Session created: ${this.sessionId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to create session:', error);
      throw error;
    }
  }

  async getSession(sessionId = null) {
    const id = sessionId || this.sessionId;
    if (!id) {
      throw new Error('No session ID provided');
    }

    try {
      const response = await this.api.get(`/chat/sessions/${id}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get session:', error);
      throw error;
    }
  }

  getSessionId() {
    return this.sessionId;
  }

  setSessionId(sessionId) {
    this.sessionId = sessionId;
  }

  // Chat Methods
  async sendMessage(message, uiEvent = null, context = null) {
    if (!this.sessionId) {
      throw new Error('No active session. Create a session first.');
    }

    try {
      // Ensure message is never null - use empty string instead
      const sanitizedMessage = message || "";
      
      // Validate and sanitize UI event
      let sanitizedUiEvent = null;
      if (uiEvent) {
        // For option events with ids (batch selection), keep it simple
        if (uiEvent.type === 'option' && uiEvent.ids) {
          sanitizedUiEvent = {
            type: 'option',
            ids: uiEvent.ids
          };
        }
        // For option events with single id
        else if (uiEvent.type === 'option' && uiEvent.id) {
          sanitizedUiEvent = {
            type: 'option',
            id: uiEvent.id
          };
        }
        // For other events, preserve all fields
        else {
          sanitizedUiEvent = {
            type: uiEvent.type || "unknown",
            ...(uiEvent.action && { action: uiEvent.action }),
            ...uiEvent
          };
        }
        console.log('🧹 Sanitized UI event:', sanitizedUiEvent);
      }

      const payload = {
        session_id: this.sessionId,
        message: sanitizedMessage,  // Never null, always string
        ui_event: sanitizedUiEvent,
        context: context || {}
      };

      console.log('📦 Sending payload:', payload);
      const response = await this.api.post('/chat/chat', payload);
      return response.data;
    } catch (error) {
      console.error('Failed to send message:', error);
      
      // Enhanced error logging for debugging
      if (error.response?.status === 500) {
        console.error('🚨 Server Error Details:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data,
          message: message,
          uiEvent: uiEvent
        });
      }
      
      throw error;
    }
  }

  async handleUIEvent(event) {
    return await this.sendMessage(null, event);
  }

  async sendChipSelection(chipId, chipData = null) {
    const uiEvent = {
      type: 'chip',
      id: chipId,
      action: 'select',
      data: chipData
    };
    return await this.handleUIEvent(uiEvent);
  }

  async sendCTAClick(action, args = {}) {
    const uiEvent = {
      type: 'cta',
      action: action,
      args: args
    };
    return await this.handleUIEvent(uiEvent);
  }

  async sendOptionSelection(optionId, groupTitle, optionData = null) {
    const uiEvent = {
      type: 'option',
      id: optionId,
      action: 'select',
      group: groupTitle,
      data: optionData
    };
    return await this.handleUIEvent(uiEvent);
  }

  async sendQuickReply(questionText, intent = null) {
    const uiEvent = {
      type: 'quick_reply',
      message: questionText,
      intent: intent
    };
    return await this.handleUIEvent(uiEvent);
  }

  // Analytics Methods
  async getSessionAnalytics(sessionId = null) {
    const id = sessionId || this.sessionId;
    if (!id) {
      throw new Error('No session ID provided');
    }

    try {
      const response = await this.api.get(`/analytics/sessions/${id}/analytics`);
      return response.data;
    } catch (error) {
      console.error('Failed to get analytics:', error);
      throw error;
    }
  }

  async getPerformanceMetrics(sessionId = null) {
    const id = sessionId || this.sessionId;
    if (!id) {
      throw new Error('No session ID provided');
    }

    try {
      const response = await this.api.get(`/analytics/sessions/${id}/performance`);
      return response.data;
    } catch (error) {
      console.error('Failed to get performance metrics:', error);
      throw error;
    }
  }

  async getExtractionDetails(sessionId = null) {
    const id = sessionId || this.sessionId;
    if (!id) {
      throw new Error('No session ID provided');
    }

    try {
      const response = await this.api.get(`/analytics/sessions/${id}/extraction-details`);
      return response.data;
    } catch (error) {
      console.error('Failed to get extraction details:', error);
      throw error;
    }
  }

  async getRagPerformance(sessionId = null) {
    const id = sessionId || this.sessionId;
    if (!id) {
      throw new Error('No session ID provided');
    }

    try {
      const response = await this.api.get(`/analytics/sessions/${id}/rag-performance`);
      return response.data;
    } catch (error) {
      console.error('Failed to get RAG performance:', error);
      throw error;
    }
  }

  async getLlmUsage(sessionId = null) {
    const id = sessionId || this.sessionId;
    if (!id) {
      throw new Error('No session ID provided');
    }

    try {
      const response = await this.api.get(`/analytics/sessions/${id}/llm-usage`);
      return response.data;
    } catch (error) {
      console.error('Failed to get LLM usage:', error);
      throw error;
    }
  }

  // Comprehensive Analytics
  async getComprehensiveAnalytics(sessionId = null) {
    const id = sessionId || this.sessionId;
    if (!id) {
      throw new Error('No session ID provided');
    }

    try {
      const [
        analytics,
        performance,
        extraction,
        ragPerformance,
        llmUsage
      ] = await Promise.all([
        this.getSessionAnalytics(id),
        this.getPerformanceMetrics(id),
        this.getExtractionDetails(id),
        this.getRagPerformance(id),
        this.getLlmUsage(id)
      ]);

      return {
        analytics,
        performance,
        extraction,
        ragPerformance,
        llmUsage,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      console.error('Failed to get comprehensive analytics:', error);
      throw error;
    }
  }

  // Health Check
  async healthCheck() {
    try {
      const response = await this.api.get('/health');
      return response.data;
    } catch (error) {
      console.error('Health check failed:', error);
      throw error;
    }
  }

  // Session Management
  async endSession() {
    if (this.sessionId) {
      console.log(`🔚 Session ended: ${this.sessionId}`);
      this.sessionId = null;
    }
  }

  // Escalation Methods
  async submitEscalation(escalationData) {
    try {
      console.log('📋 Submitting escalation:', escalationData);
      const response = await this.api.post('/escalations', escalationData);
      console.log('✅ Escalation submitted successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to submit escalation:', error);
      throw error;
    }
  }

  async getEscalation(ticketId) {
    try {
      const response = await this.api.get(`/escalations/${ticketId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get escalation:', error);
      throw error;
    }
  }

  async updateEscalationStatus(ticketId, status) {
    try {
      const response = await this.api.patch(`/escalations/${ticketId}/status`, {
        status: status
      });
      return response.data;
    } catch (error) {
      console.error('Failed to update escalation status:', error);
      throw error;
    }
  }

  async listEscalations(filters = {}) {
    try {
      const params = new URLSearchParams();
      
      // Add filters to query params
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '' && value !== 'all') {
          params.append(key, value);
        }
      });
      
      const response = await this.api.get(`/escalations?${params}`);
      return response.data;
    } catch (error) {
      console.error('Failed to list escalations:', error);
      throw error;
    }
  }

  // User Management Methods
  async createUser(userData) {
    try {
      console.log('👤 Creating user:', userData);
      const response = await this.api.post('/users/create', userData);
      console.log('✅ User created successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to create user via API:', error.message);
      
      // Fall back to mock service for development/testing
      console.log('🔧 Falling back to mock user service...');
      try {
        const mockUser = await this.mockUserService.createUser(userData);
        console.log('✅ User created via mock service:', mockUser);
        return mockUser;
      } catch (mockError) {
        console.error('❌ Mock user service also failed:', mockError.message);
        throw new Error(`User creation failed: ${error.message}`);
      }
    }
  }

  async getUser(userId) {
    try {
      const response = await this.api.get(`/users/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get user:', error);
      
      // Fall back to mock service
      try {
        return await this.mockUserService.getUser(userId);
      } catch (mockError) {
        throw error; // Throw original error
      }
    }
  }

  async updateUser(userId, userData) {
    try {
      console.log('👤 Updating user:', userId, userData);
      const response = await this.api.patch(`/users/${userId}`, userData);
      console.log('✅ User updated successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to update user:', error);
      
      // Fall back to mock service
      try {
        return await this.mockUserService.updateUser(userId, userData);
      } catch (mockError) {
        throw error; // Throw original error
      }
    }
  }

  // Utility Methods
  isSessionActive() {
    return !!this.sessionId;
  }

  async testConnection() {
    try {
      await this.healthCheck();
      console.log('✅ Connection to Boris API successful');
      return true;
    } catch (error) {
      console.error('❌ Connection to Boris API failed:', error);
      return false;
    }
  }
}

// Create and export a singleton instance
const borisApiService = new BorisApiService();
export default borisApiService;
